/**
 * The Google Drive chooser as a material row uses it: whether the tenant has
 * one, where it opens, what went wrong, and the keyboard returned to the
 * control that opened it.
 *
 * ADR 0025. The chooser opens on the person's own Drive with the course's
 * folder as a second tab when the course has a ready one. The container read
 * shares the affordance's cache entry; `retry: false` because a 404 is "this
 * tenant has no container surface", which is the same answer as no container.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  driveContainersApi,
  driveContainersQueryKey,
  readyCourseFolderId,
} from "@/lib/api/driveContainers";
import { chooseGoogleDriveFile, type GoogleDriveSelection } from "@/lib/google-drive-picker";
import { googleDrivePickerConfigForHostname } from "@/lib/google-drive-picker-config";

import { DRIVE_FAILURE, DRIVE_UNSUPPORTED } from "./material-facts";

export function useDriveChooser(courseId: string, active = true) {
  const hostConfig = googleDrivePickerConfigForHostname();
  const containers = useQuery({
    queryKey: driveContainersQueryKey(courseId),
    queryFn: () => driveContainersApi.list(courseId),
    retry: false,
    enabled: active && hostConfig.enabled,
  });
  const folderId = Array.isArray(containers.data?.containers)
    ? readyCourseFolderId(containers.data.containers)
    : null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const restoreFocus = useRef(false);
  const request = useRef<AbortController | null>(null);

  useEffect(() => () => {
    request.current?.abort();
    request.current = null;
  }, []);

  useEffect(() => {
    if (busy || !restoreFocus.current) return;
    restoreFocus.current = false;
    triggerRef.current?.focus();
  }, [busy]);

  /** The chosen file, or null when the chooser was closed or failed. */
  const choose = async (): Promise<GoogleDriveSelection | null> => {
    if (!active || !hostConfig.enabled || request.current) return null;
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setError(null);
    try {
      const outcome = await chooseGoogleDriveFile({ ...hostConfig, parentFolderId: folderId }, undefined, controller.signal);
      if (controller.signal.aborted) return null;
      if (outcome.kind === "picked") return outcome.selection;
      if (outcome.kind === "failed") setError(DRIVE_FAILURE[outcome.reason] ?? DRIVE_FAILURE.unresponsive);
      if (outcome.kind === "invalid") setError(DRIVE_UNSUPPORTED);
      return null;
    } catch {
      setError(DRIVE_FAILURE.unresponsive);
      return null;
    } finally {
      if (request.current === controller) {
        request.current = null;
        restoreFocus.current = true;
        setBusy(false);
      }
    }
  };

  return {
    enabled: active && hostConfig.enabled,
    /** A course folder is ready, so the chooser offers it as a second tab and an upload tab into it. */
    folderReady: folderId !== null,
    busy,
    error,
    triggerRef,
    choose,
    cancel: () => request.current?.abort(),
  };
}

export type DriveChooser = ReturnType<typeof useDriveChooser>;
